import React, {
  useEffect,
  useRef,
  ReactNode,
  CSSProperties,
} from 'react';
import ReactDOM from 'react-dom';

interface PortalProps {
  children: ReactNode;
  isOpen: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  onClickOutside?: () => void;
  className?: string;
  style?: CSSProperties;
}

const Portal = ({
  children,
  isOpen,
  anchorRef,
  onClickOutside,
  className = '',
  style = {},
}: PortalProps) => {
  const portalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        if (portalRef.current && anchorRef.current) {
          const rect = anchorRef.current.getBoundingClientRect();
          portalRef.current.style.position = 'fixed';
          portalRef.current.style.top = `${rect.bottom}px`;
          portalRef.current.style.left = `${rect.left}px`;
          portalRef.current.style.zIndex = '99999';
        }
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (isOpen && onClickOutside) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          portalRef.current &&
          !portalRef.current.contains(event.target as Node) &&
          anchorRef?.current &&
          !anchorRef.current.contains(event.target as Node)
        ) {
          onClickOutside();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClickOutside, anchorRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={portalRef}
      className={className}
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
};

export default Portal;
